import bugsnagClient from 'JS/global/bugsnag';
import { AutomotiveSchedulerStep, IAppointmentInfoUpdateRequest, IContactInfoUpdateRequest, IServiceInfoUpdateRequest, IWorkOrderSessionResource, PrepaymentOption, WorkOrderError, WorkOrderErrorType, WorkOrderRedirect, WorkOrderRedirectType } from '../types/session';
import { ccexp } from 'JS/components/blain-form/validation';
import { createStepChangeLogger } from 'JS/automotive/analytics';
import { CreditCardDetails } from '../../../types/CyberSource';
import { cyberSourceErrors } from 'JS/constants/cybersource.constants';
import { DEFAULT_GLOBAL_ERROR_MESSAGE } from 'JS/constants/errors';
import { defineStore } from 'pinia';
import { delay } from 'JS/utilities/promise';
import { getPreviousStep } from '../utils/steps';
import { ICustomerVehicle, PaymentTokenizationResult } from 'JS/types/Automotive';
import { ICyberSourceMicroformContext } from 'JS/types/Payment';
import { IServiceRegistry, ServiceRegistry } from '../services';
import { useGlobalLoader } from './global-loader';

export function createOrderStore({
	logService,
	cyberSourceMicroformsService,
	stepService,
	dispatchSchedulerEvent,
}: IServiceRegistry) {
	const logStepChange = createStepChangeLogger(dispatchSchedulerEvent);
	const storeComposable = defineStore("order", {
		state: () => {
			return {
				currentStep: AutomotiveSchedulerStep.ContactInfo,
				session: null as IWorkOrderSessionResource | null,
				globalError: null as WorkOrderError | null,
			};
		},
		getters: {
			canBookAppointment(): boolean {
				const { loading } = useGlobalLoader();

				return this.currentStep === AutomotiveSchedulerStep.Review && !loading.value;
			},
			totalCoveredByGiftCards(): boolean {
				const paymentInfo = this.session?.paymentInfo;
				const paymentMethods = this.session?.paymentInfo?.paymentMethods ?? [];
				const balanceCovered = !!paymentInfo?.prePay && !paymentInfo.hasRemainingBalance;
				return balanceCovered && paymentMethods.length > 0 && paymentMethods.every(pm => pm.giftCardNum);
			},
		},
		actions: {
			setSession(session: IWorkOrderSessionResource) {
				if (handleRedirect(session.redirect)) {
					return;
				}

				const nextStep = getNextStepFromSession(this.currentStep, session);

				const isInitialSessionLoad = this.session == null;
				const vehiclePresenceChanged =
					nextStep === AutomotiveSchedulerStep.Services
					&& !!session?.workOrderSummary?.vehicleInfo !== !!this.session?.workOrderSummary?.vehicleInfo;

				logStepChange({
					currentStep: this.currentStep,
					nextStep,
					session,
					force: isInitialSessionLoad || vehiclePresenceChanged,
				});

				this.currentStep = nextStep;

				if (!this.globalError && session.error) {
					this.globalError = session.error;
				}

				this.session = session;
			},
			setGlobalErrorMessage(
				message: string,
				hideRestartOption: boolean = false,
				type: WorkOrderErrorType = WorkOrderErrorType.Global) {
				this.globalError = {
					type,
					hideRestartOption,
					message,
				};
			},
			clearGlobalError() {
				this.globalError = null;
			},
			async fetchSession() {
				await withLoader('fetch-session', async () => {
					const session = await delay(stepService.getSession(), 750);
					this.setSession(session);
					const url = new URL(window.location.href);
					const paymentStep = url.searchParams.get('payment');
					const cardError = url.searchParams.get('carderror');
					if (paymentStep || cardError) {
						this.goToStep(AutomotiveSchedulerStep.Payment);
						url.searchParams.delete('payment');
						window.history.replaceState({}, "", url);
					}
				});
			},
			async setVehicle(vehicle: ICustomerVehicle) {
				await withLoader('set-vehicle', async () => {
					const session = await stepService.setVehicle(vehicle);
					this.setSession(session);
				});
			},
			goToPreviousStep() {
				this.goToStep(getPreviousStep(this.currentStep));
			},
			goToStep(nextStep: AutomotiveSchedulerStep | null) {
				if (nextStep == null || nextStep > this.session.maximumStep) {
					return;
				}

				logStepChange({
					currentStep: this.currentStep,
					nextStep,
					session: this.session
				});

				this.currentStep = nextStep;
			},
			async submitContactInfo(request: IContactInfoUpdateRequest) {
				await withLoader('submit-contact-step', async () => {
					const result = await stepService.submitContactInfo(request);
					this.setSession(result);
				});
			},
			async updateServices(selectedServices: string[]) {
				await withLoader('update-services', async () => {
					const result = await stepService.updateServices(selectedServices);
					this.setSession(result);
				});
			},

			async submitServiceInfo(request: IServiceInfoUpdateRequest) {
				await withLoader('submit-service-step', async () => {
					const result = await stepService.submitServiceStep(request);
					this.setSession(result);
				});
			},

			async submitAppointmentInfo(request: IAppointmentInfoUpdateRequest) {
				await withLoader('submit-appointment-step', async () => {
					let response: IWorkOrderSessionResource | null;
					switch (request.submissionType) {
						case 'dropoff':
							response = await stepService.requestDropoffAppointment({
								date: request.dropoffDate
							});
							break;
						case 'time-slot':
							response = await stepService.requestTimeSlotAppointment({
								timeSlot: request.timeSlot
							});
							break;
						default:
							throw Error("unhandled submission of invalid appointment form");
					}
					this.setSession(response);
				});
			},

			async getCSMicroformContext(): Promise<ICyberSourceMicroformContext> {
				return await cyberSourceMicroformsService.getMicroformContext();
			},

			isValidMicroform(cardInfo: CreditCardDetails, microform: Microform | null): boolean {
				if (!microform) { //sanity check
					return false;
				}

				if (cardInfo.inlineValidationError) {
					return false;
				}

				// Compiling MM & YY into optional parameters
				const date = cardInfo.ccExpDate;
				if (date.length < 5 || !this.isValidCardDate(cardInfo.ccExpDate)) {
					return false;
				}

				return true;
			},

			async tryGetTransientToken(cardInfo: CreditCardDetails, microform: Microform, captureContext: string): Promise<PaymentTokenizationResult> {
				if (!this.isValidMicroform(cardInfo, microform)) {
					return {
						success: false
					};
				}

				let result: PaymentTokenizationResult;
				await withLoader('tokenize-payment', async () => {

					let expMonth = "";
					let expYear = "";

					const date = cardInfo.ccExpDate;
					if (date.length > 4 && this.isValidCardDate(cardInfo.ccExpDate)) {
						const dateArray = date.split('/');
						expMonth = dateArray[0];
						expYear = "20" + dateArray[1];
					}

					const options = {
						expirationMonth: expMonth,
						expirationYear: expYear
					};

					const timeout = new Promise<string>((resolve) => {
						setTimeout(resolve, 2000, "timeout");
					});

					const tokenize = new Promise<PaymentTokenizationResult>(resolve => {
						microform.createToken(options, async function (err, token) {
							if (err) {
								switch (err.reason) {
									case 'CREATE_TOKEN_VALIDATION_PARAMS':
									case 'CREATE_TOKEN_VALIDATION_FIELDS':
										cardInfo.inlineValidationError = 'Invalid card format.';
										resolve({
											success: false
										});
										break;
									case 'CREATE_TOKEN_NO_FIELDS_LOADED':
									case 'CREATE_TOKEN_TIMEOUT':
									case 'CREATE_TOKEN_NO_FIELDS':
									case 'CREATE_TOKEN_VALIDATION_SERVERSIDE':
									case 'CREATE_TOKEN_UNABLE_TO_START':
									default:
										bugsnagClient.notify(err, {
											metaData: {
												location: 'AutomotiveScheduler',
												info: 'Failed to submit CyberSource flex microform'
											},
										});
								}
								resolve({
									success: false,
									globalErrorMessage: cyberSourceErrors.DEFAULT_ERROR
								});
							} else {
								let success = false;
								success = await cyberSourceMicroformsService.createCardWithToken({
									cardType: cardInfo.ccCardType,
									transientToken: token,
									captureContext: captureContext,
									expMonth: expMonth,
									expYear: expYear,
									isAutomotiveOrder: true,
									billingAddress: cardInfo.billingAddress,
									clearSavedCards: true,
									saveCardToAccount: false,
									makeDefault: cardInfo.makeDefault,
								});
								if (!success) {
									resolve({
										success: false,
										globalErrorMessage: cyberSourceErrors.DEFAULT_ERROR
									});
								}
								resolve({
									success: true
								});
							}
						});
					});

					const promiseResult: string | PaymentTokenizationResult = await Promise.race([tokenize, timeout]);
					if (typeof promiseResult === "string") {
						bugsnagClient.notify("Timeout on cybersource flex microform submission", {
							metaData: {
								location: 'AutomotiveScheduler'
							},
						});
						result = {
							success: false,
							globalErrorMessage: cyberSourceErrors.CONNECTION_ERROR_AUTO
						};
					} else {
						result = promiseResult;
					}
				});

				return result;
			},

			isValidCardDate(expiryDate: string): boolean {
				const validExpiryDate = ccexp(expiryDate);
				return validExpiryDate === true;
			},

			async submitPayment(prePay: boolean) {
				await withLoader('submit-payment', async () => {
					const result = await stepService.submitPaymentStep(prePay);
					this.setSession(result);
				});
			},

			async bookAppointment() {
				if (!this.canBookAppointment) {
					return;
				}

				await withLoader('book-appointment', async () => {
					const response = await stepService.submit();
					// short circuit here so that page continues loading while leaving scheduler and to prevent double-submission
					if (handleRedirect(response.redirect)) {
						return true;
					}

					this.setSession(response);
					return false;
				});
			},
		}
	});

	async function withLoader(
		name: string,
		// If this returns Promise<true>, then loading will not be stopped unless an error is thrown
		action: () => Promise<void> | Promise<boolean>,
		{
			onError = (e: any, store: ReturnType<typeof storeComposable>) => {
				logService.log(e);
				store.setGlobalErrorMessage(DEFAULT_GLOBAL_ERROR_MESSAGE);
			},
			preventWhenAlreadyLoading = true,
		} = {}
	) {
		const { createChildLoader, loading } = useGlobalLoader();
		const storeInstance = storeComposable();

		if (preventWhenAlreadyLoading && loading.value) {
			return;
		}

		const localLoading = createChildLoader(name);
		localLoading.value = true;

		let keepLoading = false;
		try {
			keepLoading = !!(await action());
		} catch (e) {
			onError(e, storeInstance);
			keepLoading = false;
		}

		if (!keepLoading) {
			localLoading.value = false;
		}
	}

	return storeComposable;
}

/**
 * Interprets redirect object returned by backend. Returns true if navigation will occur.
 */
function handleRedirect(redirect: WorkOrderRedirect): boolean {
	switch (redirect.type) {
		case WorkOrderRedirectType.Completed:
			const { orderId, orderHash } = redirect;
			window.location.href = `/automotive-scheduler/thank-you/${orderId}/${encodeURIComponent(orderHash)}`;
			return true;
		case WorkOrderRedirectType.Expired:
			window.location.href = '/automotive-service/';
			return true;
		case WorkOrderRedirectType.None:
		default:
			return false;
	}
}

function getNextStepFromSession(currentStep: AutomotiveSchedulerStep, session: IWorkOrderSessionResource) {
	if (session.setStep != null) {
		return session.setStep;
	} else if (session.maximumStep < currentStep) {
		return session.maximumStep;
	}

	return currentStep;
}

const useOrderStore = createOrderStore(ServiceRegistry);

export { useOrderStore };